/**
 * Top-level error boundary.
 *
 * Without one, a single render-time throw anywhere in the tree unmounts the
 * whole app and leaves the merchant staring at a white rectangle inside the
 * Shopify admin — with no indication that anything is wrong or that a reload
 * would help. This is cheap insurance against that.
 */
import { Component, type ErrorInfo, type ReactNode } from 'react';

interface Props {
  children: ReactNode;
}
interface State {
  error: Error | null;
}

export default class ErrorBoundary extends Component<Props, State> {
  override state: State = { error: null };

  static getDerivedStateFromError(error: Error): State {
    return { error };
  }

  override componentDidCatch(error: Error, info: ErrorInfo): void {
    console.error('[scaleads] render error', error, info.componentStack);
  }

  override render(): ReactNode {
    const { error } = this.state;
    if (!error) return this.props.children;

    return (
      <div className="content">
        <p className="eyebrow">Something broke</p>
        <h1 className="pg-title">This screen failed to render</h1>
        <p className="pg-sub">
          Your campaigns and data are unaffected — this is a display error, not a data one. Nothing
          was changed on your ad accounts.
        </p>
        <div className="card card-p">
          <h4>Details</h4>
          <pre className="mono" style={{ whiteSpace: 'pre-wrap', margin: 0 }}>
            {error.message}
          </pre>
        </div>
        <div className="row gap-2" style={{ marginTop: 16 }}>
          <button className="btn brand" onClick={() => window.location.reload()}>
            Reload
          </button>
          <button className="btn" onClick={() => this.setState({ error: null })}>
            Try again without reloading
          </button>
        </div>
      </div>
    );
  }
}
